import { useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronRight, Book, FileCode, Code, GitPullRequest, HelpCircle, Plug } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';

interface DocSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  subsections?: { id: string; title: string }[];
}

const docSections: DocSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: <Book className="h-4 w-4" />,
    subsections: [
      { id: 'overview', title: 'Overview' },
      { id: 'browse', title: 'Browsing Standards' },
    ],
  },
  {
    id: 'schema-reference',
    title: 'Schema Reference',
    icon: <FileCode className="h-4 w-4" />,
    subsections: [
      { id: 'structure', title: 'Schema Structure' },
      { id: 'types', title: 'Field Types' },
    ],
  },
  {
    id: 'api-reference',
    title: 'API Reference',
    icon: <Plug className="h-4 w-4" />,
    subsections: [
      { id: 'authentication', title: 'Authentication' },
      { id: 'rate-limits', title: 'Rate Limits' },
      { id: 'endpoints', title: 'Endpoints' },
      { id: 'examples', title: 'Code Examples' },
    ],
  },
  {
    id: 'implementation',
    title: 'Implementation',
    icon: <Code className="h-4 w-4" />,
    subsections: [
      { id: 'typescript', title: 'TypeScript' },
      { id: 'validation', title: 'Validation' },
    ],
  },
  {
    id: 'contributing',
    title: 'Contributing',
    icon: <GitPullRequest className="h-4 w-4" />,
    subsections: [
      { id: 'propose', title: 'Proposing Standards' },
    ],
  },
  {
    id: 'faq',
    title: 'FAQ',
    icon: <HelpCircle className="h-4 w-4" />,
  },
];

interface DocsSidebarProps {
  activeSection: string;
  onSectionClick: (sectionId: string) => void;
  className?: string;
}

function SidebarContent({ activeSection, onSectionClick }: DocsSidebarProps) {
  const [expandedSections, setExpandedSections] = useState<string[]>(['getting-started']);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleClick = (sectionId: string) => {
    onSectionClick(sectionId);
  };

  const isActive = (sectionId: string) => {
    return activeSection === sectionId || activeSection.startsWith(sectionId + '-');
  };

  return (
    <nav className="space-y-1">
      {docSections.map((section) => (
        <div key={section.id}>
          <button
            onClick={() => {
              if (section.subsections) {
                toggleSection(section.id);
              }
              handleClick(section.id);
            }}
            className={cn(
              'w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors',
              isActive(section.id)
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <span className="flex items-center gap-2">
              {section.icon}
              {section.title}
            </span>
            {section.subsections && (
              expandedSections.includes(section.id) ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )
            )}
          </button>
          
          {section.subsections && expandedSections.includes(section.id) && (
            <div className="ml-6 mt-1 space-y-1 border-l border-border pl-3">
              {section.subsections.map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => handleClick(`${section.id}-${sub.id}`)}
                  className={cn(
                    'w-full text-left px-3 py-2 md:py-1.5 text-sm rounded-md transition-colors',
                    activeSection === `${section.id}-${sub.id}`
                      ? 'text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {sub.title}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>
  );
}

export function DocsSidebar({ activeSection, onSectionClick, className }: DocsSidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSectionClick = (sectionId: string) => {
    onSectionClick(sectionId);
    setMobileOpen(false);
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={cn('hidden lg:block w-64 shrink-0', className)}>
        <div className="sticky top-24 pr-4">
          <h3 className="font-semibold text-foreground mb-4 px-3">Documentation</h3>
          <SidebarContent activeSection={activeSection} onSectionClick={onSectionClick} />
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <div className="lg:hidden fixed bottom-4 right-4 z-50">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button size="icon" className="rounded-full shadow-lg h-12 w-12">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 pt-12">
            <h3 className="font-semibold text-foreground mb-4 px-3">Documentation</h3>
            <SidebarContent activeSection={activeSection} onSectionClick={handleSectionClick} />
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}

export { docSections };
